import type { Locale } from '../locales.ts';
import descriptionsJson from './descriptions.json' with { type: 'json' };
import docMetaJson from './docMeta.json' with { type: 'json' };
import hrefsJson from './hrefs.json' with { type: 'json' };
import legalValuesJson from './legalValues.json' with { type: 'json' };
import { LEGAL_PAGES, docKeyFor, type LegalPageId } from './pages.ts';
import { VERSION_SETS, type LegalVersion, type VersionSetName } from './legalVersions.ts';

export { LEGAL_PAGES, LEGAL_PAGE_IDS, docKeyFor } from './pages.ts';
export type { LegalPageDef, LegalPageId } from './pages.ts';
export { PRIVACY_VERSIONS, TERMS_VERSIONS, VERSION_SETS } from './legalVersions.ts';
export type { LegalVersion, VersionSetName } from './legalVersions.ts';

/**
 * Giá trị nội suy của văn bản pháp lý — CHÉP từ `main/src/lib/site.config.ts`, byte-identical.
 *
 * Vì sao không dùng `ORGANIZATION` của `apps/web`: (1) `ORGANIZATION.address` là chuỗi ĐƠN chứa
 * địa chỉ HIỆN HÀNH, còn `/privacy/v1/` là bản LƯU TRỮ phải giữ địa chỉ ĐƯƠNG THỜI của nó —
 * viết lại là làm sai lệch bản lưu trữ; (2) `ORGANIZATION` thuộc `apps/web/src/core/`, một
 * package khác và là hot zone của lô khác.
 *
 * ── `companyAddress` là MỘT chuỗi tiếng Việt, và đó là QUYẾT ĐỊNH ĐÃ KÝ ─────────────────────────
 *
 * **User chốt 2026-08-20 (vé #12): giữ đúng như `main`.**
 *
 * Hệ quả phải nói thẳng, không giấu: `/en/privacy/v1/` và `/en/terms/v1/` in địa chỉ **tiếng Việt**
 * (`… Phường Đa Kao, Quận 1, TP. Hồ Chí Minh.`), vì cả `privacy.v1.vi.html` lẫn `privacy.v1.en.html`
 * đều nội suy CÙNG khoá này. Reviewer đo trên bản build và báo đúng.
 *
 * Vì sao vẫn giữ, chứ không tách `{ vi, en }` như bản khai ban đầu của lô định làm:
 *   1. Giá trị hiện tại **byte-identical với `main`** — `git show main:src/lib/site.config.ts` dòng 7
 *      y hệt, kể cả `Phường Đa Kao, Quận 1`. Đây là bản LƯU TRỮ của một văn bản ĐÃ ĐĂNG KÝ.
 *   2. Bản tiếng Anh của địa chỉ THỜI-v1 **không tồn tại** ở đâu — không trong repo, không trong
 *      `main`. Sinh ra nó là VIẾT CHỮ MỚI vào một bản lưu trữ pháp lý, đúng thứ lô này bị cấm.
 *   3. Địa chỉ hiện hành thì KHÔNG bị ảnh hưởng: ở `terms` và `privacy` (bản hiện hành) địa chỉ là
 *      literal nằm trong thân văn bản của từng locale, không nội suy. Đo được: bản EN in
 *      `Sai Gon Ward, Ho Chi Minh City, Vietnam`. Chỉ 4 trang `v1` đi qua khoá này.
 *
 * ⇒ Muốn đổi thì cần user cấp bản tiếng Anh của địa chỉ THỜI-v1. Đừng tự dịch, và đừng "sửa cho
 *   khớp" với địa chỉ hiện hành — hai địa chỉ khác nhau là ĐÚNG, vì pháp nhân đã chuyển địa chỉ.
 */
export const LEGAL_VALUES: Readonly<Record<string, string>> = legalValuesJson;

const HREFS = hrefsJson as Readonly<Record<string, Readonly<Record<string, string>>>>;

/**
 * Biến thân văn bản đã trích thành HTML THẬT.
 *
 * Thân đó KHÔNG phải HTML thuần — nó là markup Svelte, nên có HAI dạng phải giải, và bỏ sót dạng
 * nào cũng ra lỗi im lặng:
 *
 *   1. `{site.appName}` → giá trị. Thay ở ĐÂY, không bake vào văn bản: `companyLegalName` xuất
 *      hiện 20 lượt, bake vào là đổi tên pháp nhân phải sửa 20 chỗ và sai âm thầm ở chỗ thứ 21.
 *
 *   2. ``href={`mailto:${site.deletionEmail}?subject=${subject}&body=${body}`}`` → `href="…"`.
 *      **16 chỗ trong 10 file.** Bỏ sót dạng này là React render nguyên chuỗi Svelte thành giá
 *      trị thuộc tính ⇒ 16 link hỏng, trong đó có đường thực hiện QUYỀN XOÁ TÀI KHOẢN. Giá trị
 *      thay vào SINH TỪ MÁY (`hrefs.json`) vì `subject`/`body` là `encodeURIComponent(...)` dựng
 *      trong khối `<script>` của `main` — không tính lại được bằng mắt.
 *
 * Thứ tự BẮT BUỘC: giải `href` TRƯỚC. Làm ngược lại thì bước 1 thay `{site.deletionEmail}` bên
 * trong `${...}` và phá vỡ khoá tra của bước 2.
 */
export function resolveLegalText(html: string, docKey: string): string {
  let out = html;

  const hrefs = HREFS[docKey];
  if (hrefs !== undefined) {
    for (const [expr, resolved] of Object.entries(hrefs)) {
      out = out.split(`href={${expr}}`).join(`href="${resolved}"`);
    }
  }

  out = out.replace(/(?<!\$)\{site\.([A-Za-z]+(?:\.[A-Za-z]{2})?)\}/g, (_whole, key: string) => {
    const value = LEGAL_VALUES[key];
    if (value === undefined) {
      throw new Error(`[legal] Thiếu giá trị cho \`site.${key}\` trong legalValues.json.`);
    }
    return value;
  });

  // Chốt: còn cú pháp Svelte nào sót lại là LỖI, không phải chuyện nhỏ — nó sẽ hiện nguyên văn
  // trên một trang pháp lý.
  if (/\{site\.|href=\{`/.test(out)) {
    throw new Error(
      `[legal] ${docKey}: còn cú pháp Svelte chưa giải. Chạy lại gen-legal-descriptions.mjs.`,
    );
  }
  return out;
}

export interface LegalDocMeta {
  readonly title: string;
  readonly showDates: boolean;
  readonly effectiveDate: string;
  readonly lastUpdated: string;
  readonly activeVersion?: string;
  readonly versions?: VersionSetName;
}

const DOC_META = docMetaJson as Readonly<Record<string, LegalDocMeta>>;
const DESCRIPTIONS = descriptionsJson as Readonly<Record<string, string>>;

function lookup<T>(table: Readonly<Record<string, T>>, key: string, what: string): T {
  const value = table[key];
  if (value === undefined) {
    throw new Error(
      `[legal] Không có ${what} cho \`${key}\` — chạy \`node script/gen-legal-descriptions.mjs\`.`,
    );
  }
  return value;
}

export function legalDocMeta(id: LegalPageId, locale: Locale): LegalDocMeta {
  return lookup(DOC_META, docKeyFor(id, locale), 'docMeta');
}

/**
 * `meta description` — TRÍCH BẰNG MÁY từ chính văn bản, không viết mới.
 * `main` không có description cho trang pháp lý ở đâu cả (đã đo), mà `PageHead` thì đòi. Trích
 * máy là đường duy nhất vừa có description vừa không viết một chữ nào.
 */
export function legalDescription(id: LegalPageId, locale: Locale): string {
  return lookup(DESCRIPTIONS, docKeyFor(id, locale), 'description');
}

/** Danh sách phiên bản của trang, hoặc `undefined` nếu văn bản này không có bộ phiên bản. */
export function legalVersions(
  id: LegalPageId,
  locale: Locale,
): readonly LegalVersion[] | undefined {
  const set = legalDocMeta(id, locale).versions;
  return set === undefined ? undefined : VERSION_SETS[set];
}

/**
 * Bản đang xem có phải bản LƯU TRỮ không.
 *
 * `main/src/lib/components/LegalPage.svelte:27` suy điều này từ DỮ LIỆU phiên bản
 * (`versions[0].id !== activeVersion`). v2 khai thẳng bằng `indexable` trong bảng route, nên hai
 * nguồn phải KHỚP — `legal.test.ts` chốt đúng chuyện đó.
 */
export function isArchived(id: LegalPageId, locale: Locale): boolean {
  const meta = legalDocMeta(id, locale);
  const versions = legalVersions(id, locale);
  if (versions === undefined || meta.activeVersion === undefined) return false;
  return versions[0]?.id !== meta.activeVersion;
}

export function isLegalPageId(value: string): value is LegalPageId {
  return Object.hasOwn(LEGAL_PAGES, value);
}
